/**
 * Relatórios — os três do §5.13 da spec 04.
 *
 * ## As duas coisas que esta tela diz em voz alta
 *
 * **1. Não existe "duração".** Existem *espera* e *atendimento*, separados. É a divergência mais útil
 * da ADR-0004: o concorrente soma os dois e mostra `16:16` para um atendimento de três minutos que
 * passou a noite na fila. A tela mostra os dois lado a lado justamente para a diferença ser visível.
 *
 * **2. Mediana e p90, nunca média** — e a tela **escreve isso**, porque alguém vai comparar com a
 * "média" de outro produto e concluir que o nosso número está errado.
 *
 * ## O que está vazio diz que está vazio
 *
 * Amostra zero devolve `null` do servidor, e aqui vira `—` com a contagem da amostra ao lado. Um `0s`
 * faria um dia sem trabalho parecer operação perfeita.
 */

import { useState, type ReactNode } from "react";
import { EstadoCarregando, EstadoDaFalha, EstadoVazio, Selo, falhaDe, identificadorLegivel, useSessao } from "@hugi/ui";
import { CabecalhoDaTela, Dica, GradeDeCartoes, PaginaInteira, PainelDeIndicadores, SemPermissao, type Indicador } from "@hugi/ui/kit";
import { TrendingUp } from "lucide-react";
import {
  duracaoCurta,
  useAgenteDeIa,
  useFunilDoFluxo,
  useRelatorioDoFunil,
  useAnuncios,
  useBacklog,
  usePoliticasDeSla,
  useRelatorioDeSla,
  useRelatorioDeConsumo,
  useSatisfacao,
  useRelatorioDeOrigem,
  useRelatorioDeVolume,
  type RelatorioDeConsumo,
  type Tempos,
} from "./dados.js";
import { useAbaDaUrl } from "./acoes-do-dono.js";
import "./hugi-relatorios.css";

const PERIODOS = [
  { dias: 1, rotulo: "Hoje" },
  { dias: 7, rotulo: "7 dias" },
  { dias: 30, rotulo: "30 dias" },
  { dias: 90, rotulo: "90 dias" },
] as const;

type Aba =
  | "volume"
  | "backlog"
  | "sla"
  | "agente"
  | "fluxo"
  | "oportunidades"
  | "origem"
  | "anuncios"
  | "satisfacao"
  | "consumo";

const ABAS: readonly { id: Aba; rotulo: string }[] = [
  { id: "volume", rotulo: "Volume e atendimento" },
  { id: "backlog", rotulo: "Backlog" },
  { id: "sla", rotulo: "SLA" },
  { id: "agente", rotulo: "Agente de IA" },
  { id: "fluxo", rotulo: "Funil do fluxo" },
  { id: "oportunidades", rotulo: "Oportunidades" },
  { id: "origem", rotulo: "Origem das conversas" },
  { id: "anuncios", rotulo: "Anúncios" },
  { id: "satisfacao", rotulo: "Satisfação" },
  { id: "consumo", rotulo: "Consumo" },
];

export function Relatorios() {
  const sessao = useSessao();
  const [dias, setDias] = useState(7);
  // A aba mora na URL (`/relatorios?aba=sla`, spec 23): o Início leva direto a um relatório, e o link sobrevive.
  const [aba, setAba] = useAbaDaUrl<Aba>(ABAS.map((a) => a.id), "volume");

  if (!sessao.permissoes.includes("read_report")) {
    return (
      <PaginaInteira rotulo="Relatórios" classe="hugi-relatorios">
        <SemPermissao>
          Relatórios exigem a permissão <code>read_report</code>. Um administrador da sua empresa
          concede.
        </SemPermissao>
      </PaginaInteira>
    );
  }

  const conteudo: Record<Aba, () => ReactNode> = {
    volume: () => <Volume dias={dias} />,
    backlog: () => <BacklogAba dias={dias} />,
    sla: () => <SlaAba dias={dias} />,
    agente: () => <AgenteAba dias={dias} />,
    fluxo: () => <FunilAba dias={dias} />,
    oportunidades: () => <OportunidadesAba dias={dias} />,
    origem: () => <Origem dias={dias} />,
    anuncios: () => <AnunciosAba dias={dias} />,
    satisfacao: () => <Satisfacao dias={dias} />,
    consumo: () => <Consumo dias={dias} />,
  };

  return (
    <PaginaInteira rotulo="Relatórios" classe="hugi-relatorios">
      <CabecalhoDaTela
        icone={<TrendingUp size={16} />}
        titulo="Relatórios"
        dica="Volume, tempos, origem, satisfação e consumo — sempre com mediana, nunca média."
        ferramentas={
          <span role="group" aria-label="Período" className="hugi-relatorios-periodo">
            {PERIODOS.map((p) => (
              <button key={p.dias} type="button" onClick={() => setDias(p.dias)} aria-pressed={dias === p.dias}>
                {p.rotulo}
              </button>
            ))}
          </span>
        }
      />

      <FaixaDoVolume dias={dias} />

      {/* Os dez relatórios são ABAS de um cartão só: o que não cabe vira aba, e o corpo rola por dentro. */}
      <GradeDeCartoes
        rotulo="Relatórios"
        cartoes={[
          {
            id: "relatorio",
            titulo: "Relatórios",
            abas: ABAS.map((a) => ({
              id: a.id,
              rotulo: a.rotulo,
              conteudo: () => <div className="hugi-relatorios-aba">{conteudo[a.id]()}</div>,
            })),
            aba,
            aoTrocarAba: (id) => setAba(id as Aba),
          },
        ]}
      />
    </PaginaInteira>
  );
}

/**
 * A faixa do topo: os números de volume do período, sempre à vista, qualquer que seja a aba. Enquanto não chegam
 * (ou se falharem) o número é "—", e não zero — a aba Volume diz o erro.
 */
function FaixaDoVolume(props: { readonly dias: number }) {
  const q = useRelatorioDeVolume(props.dias);
  const d = q.data ?? null;
  const indicadores: Indicador[] = [
    { id: "conversas", rotulo: "Conversas", valor: d ? d.recebidas : null },
    { id: "respondidas", rotulo: "Respondidas", valor: d ? d.respondidas : null },
    { id: "finalizadas", rotulo: "Finalizadas", valor: d ? d.finalizadas : null },
    {
      id: "reabertas",
      rotulo: "Reabertas",
      valor: d ? d.reabertas : null,
      // A spec diz o que este número significa: "reabertura alta é motivo mal escolhido ou problema
      // não resolvido". Sem a frase, é só um número.
      ...(d && d.reabertas > 0 ? { detalhe: "motivo mal escolhido, ou problema não resolvido", tom: "atencao" as const } : {}),
    },
    {
      id: "primeira-resposta",
      rotulo: "Até a primeira resposta",
      dica: "É o que o cliente final sente. Mediana, com o p90 ao lado: é ele que mostra a cauda.",
      valor: d ? duracaoCurta(d.primeiraResposta.medianaSegundos) : null,
      ...(d ? { detalhe: `mediana · p90 ${duracaoCurta(d.primeiraResposta.p90Segundos)}` } : {}),
    },
  ];
  return <PainelDeIndicadores faixa rotulo="Volume do período" indicadores={indicadores} />;
}

/** Um tempo, com mediana E p90. Os dois juntos, porque separá-los esconde a cauda. */
function Tempo(props: { readonly rotulo: string; readonly tempos: Tempos; readonly explicacao: string }) {
  const { tempos: t } = props;
  return (
    <div className="vidro p-4">
      <p className="flex items-center gap-1.5 text-xs text-[color:var(--tinta-fraca)]">
        {props.rotulo} <Dica texto={props.explicacao} />
      </p>
      <p className="mt-1 text-xl font-semibold text-[color:var(--tinta-forte)]">
        {duracaoCurta(t.medianaSegundos)}
      </p>
      <p className="text-[11px] text-[color:var(--tinta-fraca)]">
        {/*
          O p90 fica junto da mediana, e não numa aba avançada: é ele que mostra a cauda. Mediana de
          4 min com p90 de 6 horas é uma operação diferente de mediana de 4 min com p90 de 9 min.
        */}
        mediana · p90 {duracaoCurta(t.p90Segundos)} · {t.amostra} atendimento(s)
      </p>
    </div>
  );
}

function Volume(props: { readonly dias: number }) {
  const q = useRelatorioDeVolume(props.dias);
  if (q.isPending) return <EstadoCarregando linhas={4} />;
  if (q.error) {
    return (
      <EstadoDaFalha
        falha={falhaDe(q.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
        aoRepetir={() => void q.refetch()}
      />
    );
  }
  const d = q.data!;

  return (
    <div className="flex flex-col gap-4">
      {/* Conversas, respondidas, finalizadas e reabertas estão na faixa do topo da tela. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Tempo
          rotulo="Até a primeira resposta"
          tempos={d.primeiraResposta}
          explicacao="É o que o cliente final sente. Não é nenhum dos dois ao lado."
        />
        <Tempo
          rotulo="Espera na fila"
          tempos={d.espera}
          explicacao="De quando a conversa chegou até alguém assumir."
        />
        <Tempo
          rotulo="Tempo de atendimento"
          tempos={d.atendimento}
          explicacao="De assumir até finalizar. Somar com a espera daria um número que não descreve nada."
        />
      </div>

      {/*
        As quebras lado a lado, cada uma no seu cartão — eram cinco listas empilhadas.
      */}
      <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
      {/*
        O corte por canal é OBRIGATÓRIO — §4.3 da spec 07: "e-mail responde em horas, SMS em segundos,
        WhatsApp no meio. Uma mediana que mistura os três não descreve nenhum deles".
      */}
      <Tabela
        titulo="Por canal"
        nota="Cada canal tem ritmo próprio. Uma mediana que mistura e-mail com SMS não descreve nenhum dos dois."
        vazio="Nenhuma conversa no período."
        linhas={d.porCanal.map((c) => ({
          chave: c.canal,
          rotulo: c.canal,
          valor: String(c.atendimentos),
          extra: `1ª resposta ${duracaoCurta(c.primeiraRespostaMediana)} · espera ${duracaoCurta(c.esperaMediana)}`,
        }))}
      />

      {/*
        Por CONEXÃO, além de por canal (0055). Com dois números na conta, "WhatsApp: 22" não diz por
        onde as conversas entram — esta tabela diz. O rótulo vem do servidor como apelido ou número
        cru, e a máscara é a mesma do inbox: um número que aqui aparecesse cru e lá formatado seria a
        mesma conexão parecendo duas.
      */}
      <Tabela
        titulo="Por conexão"
        nota="Por onde as conversas entram. Dê um apelido à conexão em Canais para ler o nome em vez do número."
        vazio="Nenhuma conversa no período."
        linhas={d.porConexao.map((c) => ({
          chave: c.conexaoId ?? "sem-conexao",
          rotulo: identificadorLegivel(c.rotulo),
          valor: String(c.atendimentos),
          extra: c.canal,
        }))}
      />

      <Tabela
        titulo="Por fila"
        vazio="Nenhuma conversa no período."
        linhas={d.porFila.map((f) => ({
          chave: f.filaId ?? "sem-fila",
          rotulo: f.nome,
          valor: String(f.atendimentos),
        }))}
      />

      <Tabela
        titulo="Por atendente"
        nota="Só pessoas. O que o agente Hugi atendeu não entra aqui."
        vazio="Nenhum atendimento assumido no período."
        linhas={d.porAtendente.map((a) => ({
          chave: a.atendenteId,
          rotulo: a.nome,
          valor: String(a.atendimentos),
          extra: `atendimento ${duracaoCurta(a.atendimentoMediana)}`,
        }))}
      />

      <Tabela
        titulo="Por motivo de encerramento"
        nota="Automáticos aparecem marcados: encerramento por inatividade não é o mesmo que alguém decidir que resolveu."
        vazio="Nenhum atendimento finalizado no período."
        linhas={d.porMotivo.map((m) => ({
          chave: m.motivoId ?? "sem-motivo",
          rotulo: m.nome,
          valor: String(m.atendimentos),
          selo: m.automatico ? "automático" : undefined,
        }))}
      />
      </div>
    </div>
  );
}

function Origem(props: { readonly dias: number }) {
  const q = useRelatorioDeOrigem(props.dias);
  if (q.isPending) return <EstadoCarregando linhas={3} />;
  if (q.error) {
    return (
      <EstadoDaFalha
        falha={falhaDe(q.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
        aoRepetir={() => void q.refetch()}
      />
    );
  }
  const d = q.data!;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <Cartao rotulo="Conversas no período" valor={d.total} />
        <Cartao
          rotulo="Vindas de anúncio"
          valor={d.deAnuncio}
          nota={d.total > 0 ? `${Math.round((d.deAnuncio / d.total) * 100)}% do total` : undefined}
        />
      </div>

      <Tabela
        titulo="Por anúncio"
        nota="Vem do referral que a Meta manda na PRIMEIRA mensagem da conversa — ela não reenvia, então o que não chegou na hora não volta."
        vazio="Nenhuma conversa de anúncio no período. Quando alguém clicar em um anúncio do WhatsApp, ela aparece aqui com o título do anúncio."
        linhas={d.porAnuncio.map((a) => ({
          chave: a.anuncioId ?? "sem-id",
          rotulo: a.titulo ?? a.anuncioId ?? "(anúncio sem título)",
          valor: String(a.conversas),
          extra: `${a.respondidas} respondida(s)${a.tipoDeOrigem ? ` · ${a.tipoDeOrigem}` : ""}`,
        }))}
      />
    </div>
  );
}

function Consumo(props: { readonly dias: number }) {
  const q = useRelatorioDeConsumo(props.dias);
  if (q.isPending) return <EstadoCarregando linhas={3} />;
  if (q.error) {
    return (
      <EstadoDaFalha
        falha={falhaDe(q.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
        aoRepetir={() => void q.refetch()}
      />
    );
  }
  const d = q.data!;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Cartao rotulo="Enviadas" valor={d.enviadas} />
        <Cartao rotulo="Recebidas" valor={d.recebidas} />
        <Cartao rotulo="Por template" valor={d.porTemplate} nota="template tem regra de cobrança própria" />
        <Cartao rotulo="Marcadas como cobráveis" valor={d.cobraveisConhecidas} />
      </div>

      <CategoriasDeCobranca d={d} />

      {/*
        A LACUNA vem do servidor e é mostrada, não reescrita. O §5.13(c) manda declará-la: um número que
        se apresenta como fatura e não é geraria contestação no primeiro mês.
      */}
      <p role="note" className="rounded px-3 py-2 text-xs" style={{ background: "var(--atencao-clara)", color: "var(--atencao-texto)" }}>
        {d.lacuna}
      </p>
    </div>
  );
}

/**
 * A quebra por categoria de cobrança da Meta — §2.8 do bench do ContactFy.
 *
 * ## Três decisões, e as três são sobre não mentir
 *
 * 1. **Sem categoria nenhuma, a tabela NÃO aparece** — nem vazia, nem com "0". Um tenant só no canal por
 *    aparelho vinculado nunca terá categoria, e uma tabela zerada ali leria como "você não manda nada".
 *    No lugar vai a frase do servidor, que explica **por que** não há.
 * 2. **`semCategoria` fica FORA da tabela**, num rodapé. Ele não é uma categoria; pô-lo como linha faria
 *    o canal não oficial parecer item de fatura da Meta.
 * 3. **Nenhum valor em R$.** O que a tabela conta é mensagem; preço é da Meta e muda — a tarifa de
 *    01/10/2026 é o exemplo vivo. Um número em reais aqui envelheceria calado e contestaria a fatura.
 */
function CategoriasDeCobranca(props: { readonly d: RelatorioDeConsumo }) {
  const { porCategoria, semCategoria, porqueSemCategoria } = props.d;

  if (porCategoria.length === 0) {
    return (
      <p role="note" className="text-xs text-[color:var(--tinta-fraca)]">
        Nenhum envio com categoria de cobrança neste período. {porqueSemCategoria}
      </p>
    );
  }

  return (
    <div className="vidro p-4">
      <h3 className="text-sm font-semibold text-[color:var(--tinta-forte)]">
        Por categoria de cobrança{" "}
        <Dica texto="A categoria vem do recibo da Meta, mensagem a mensagem. “Cobráveis” é o que ela marcou como cobrado — não é conta nossa." />
      </h3>

      {/*
        A tabela rola DENTRO do cartão, e não a página.

        Medido a 375px: a tabela pede 284px e o cartão oferece 235, porque a barra lateral ainda ocupa
        105px quando a pessoa a deixou aberta (é o H68, e não é desta frente). Cartão com grade de números
        reflui; tabela não — ela transborda calada. Sem este `overflow-x`, a coluna "Cobráveis" ficaria
        cortada no celular, que é justamente a coluna que este relatório existe para mostrar.
      */}
      <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-[color:var(--tinta-fraca)]">
            <th className="pb-1 font-normal">Categoria</th>
            <th className="pb-1 text-right font-normal">Mensagens</th>
            <th className="pb-1 text-right font-normal">Cobráveis</th>
          </tr>
        </thead>
        <tbody>
          {porCategoria.map((c) => (
            <tr key={c.categoria} className="border-t border-[color:var(--borda)]">
              <td className="py-1.5 text-[color:var(--tinta)]">{c.rotulo}</td>
              <td className="py-1.5 text-right tabular-nums text-[color:var(--tinta)]">{c.mensagens}</td>
              <td className="py-1.5 text-right tabular-nums text-[color:var(--tinta)]">{c.cobraveis}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {semCategoria > 0 ? (
        <p className="mt-3 text-[11px] text-[color:var(--tinta-fraca)]">
          Mais {semCategoria} envio(s) sem categoria. {porqueSemCategoria}
        </p>
      ) : null}
    </div>
  );
}

function Cartao(props: { readonly rotulo: string; readonly valor: number; readonly nota?: string | undefined }) {
  return (
    <div className="vidro p-4">
      <p className="text-xs text-[color:var(--tinta-fraca)]">{props.rotulo}</p>
      <p className="mt-1 text-2xl font-semibold text-[color:var(--tinta-forte)]">{props.valor}</p>
      {props.nota ? <p className="text-[11px] text-[color:var(--tinta-fraca)]">{props.nota}</p> : null}
    </div>
  );
}

function Tabela(props: {
  readonly titulo: string;
  readonly nota?: string;
  readonly vazio: string;
  readonly linhas: readonly {
    chave: string;
    rotulo: string;
    valor: string;
    extra?: string | undefined;
    selo?: string | undefined;
  }[];
}) {
  return (
    <div className="vidro p-3">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold">
        {props.titulo}
        {props.nota ? <Dica texto={props.nota} /> : null}
      </h2>

      {props.linhas.length === 0 ? (
        // Vazio com EXPLICAÇÃO, e não "nenhum resultado": §12 da spec 05.
        <p className="mt-1.5 text-xs text-[color:var(--tinta-fraca)]">{props.vazio}</p>
      ) : (
        <ul className="mt-1.5 flex flex-col gap-1">
          {props.linhas.map((l) => (
            <li key={l.chave} className="flex flex-wrap items-center gap-2 border-t border-[color:var(--borda)] py-1.5">
              <span className="flex-1 truncate text-sm">{l.rotulo}</span>
              {l.selo ? <Selo estado="neutro" rotulo={l.selo} /> : null}
              {l.extra ? (
                <span className="text-[11px] text-[color:var(--tinta-fraca)]">{l.extra}</span>
              ) : null}
              <span className="font-mono text-sm font-medium">{l.valor}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Satisfação.
 *
 * ## O que esta aba NÃO mostra
 *
 * **Média.** Duas notas 5 e uma nota 1 dão 3,67 — que não descreve nenhum dos três clientes. O que fica é a
 * distribuição, a taxa de resposta e os comentários; a taxa é o número que diz se a pesquisa serve para
 * algo, porque 4,8 em 3 respostas de 200 pedidos é ruído, não informação.
 */
function Satisfacao(props: { readonly dias: number }) {
  const q = useSatisfacao(props.dias);

  if (q.isPending) return <EstadoCarregando rotulo="Carregando a satisfação" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  const s = q.data;
  const maior = Math.max(1, ...s.distribuicao);

  if (s.pedidas === 0) {
    return (
      <EstadoVazio
        titulo="Nenhuma pesquisa pedida no período"
        comoEncher="Ligue a pesquisa de satisfação em Ajustes. Ela sai quando o atendimento é encerrado."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-6">
        <div>
          <p className="text-2xl font-medium tabular-nums">{s.respondidas}</p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">respostas de {s.pedidas} pedidos</p>
        </div>
        <div>
          <p className="text-2xl font-medium tabular-nums">
            {Math.round((s.respondidas / s.pedidas) * 100)}%
          </p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">taxa de resposta</p>
        </div>
        <div>
          {/* `null` não é 0%: "ninguém respondeu" e "todos deram nota baixa" são coisas diferentes. */}
          <p className="text-2xl font-medium tabular-nums">
            {s.percentualBom === null ? "—" : `${s.percentualBom}%`}
          </p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">deram 4 ou 5</p>
        </div>
      </div>

      <div className="vidro flex flex-col gap-2 p-4">
        {[5, 4, 3, 2, 1].map((nota) => {
          const quantas = s.distribuicao[nota - 1] ?? 0;
          return (
            <div key={nota} className="flex items-center gap-2">
              <span className="w-6 text-sm tabular-nums">{nota}</span>
              <div className="h-3 flex-1 overflow-hidden rounded-full bg-[color:var(--realce)]">
                <div
                  className="h-full rounded-full bg-[color:var(--acao)]"
                  style={{ width: `${(quantas / maior) * 100}%` }}
                />
              </div>
              <span className="w-10 text-right text-sm tabular-nums">{quantas}</span>
            </div>
          );
        })}
      </div>

      {s.comentarios.length > 0 && (
        <div className="vidro flex flex-col gap-2 p-4">
          <p className="text-sm font-medium">O que escreveram <Dica texto="É onde está o que o número não diz." /></p>
          <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
            {s.comentarios.map((c, i) => (
              <li key={i} className="flex items-baseline gap-2 py-1.5 text-sm">
                <span className="shrink-0 tabular-nums">{c.nota}</span>
                <span className="min-w-0 flex-1">{c.comentario}</span>
                <span className="shrink-0 text-xs text-[color:var(--tinta-fraca)]">
                  {new Date(c.em).toLocaleDateString("pt-BR")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Backlog — `abertas − fechadas`, por dia (§5.1 da spec 09).
 *
 * ## Por que a série diária, e não um número
 *
 * `+40` no período pode ser um pico de segunda já absorvido, ou quatro dias de `+10` seguidos — e as duas
 * coisas pedem decisões opostas. O número que **alerta** é `diasSeguidosPositivos`, porque tempo de resposta
 * continua bom enquanto a fila cresce, até o dia em que desaba.
 */
function BacklogAba(props: { readonly dias: number }) {
  const q = useBacklog(props.dias);

  if (q.isPending) return <EstadoCarregando rotulo="Carregando o backlog" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  const b = q.data;
  const maior = Math.max(1, ...b.dias.map((d) => Math.max(d.abertas, d.fechadas)));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-6">
        <Numero valor={b.abertasNoPeriodo} rotulo="abriram" />
        <Numero valor={b.fechadasNoPeriodo} rotulo="fecharam" />
        <Numero
          valor={`${b.saldoNoPeriodo > 0 ? "+" : ""}${b.saldoNoPeriodo}`}
          rotulo="saldo do período"
          atencao={b.saldoNoPeriodo > 0}
        />
        <Numero valor={b.abertasAgora} rotulo="em aberto agora" />
      </div>

      {b.diasSeguidosPositivos >= 3 ? (
        <p className="text-sm text-[color:var(--atencao-texto)]">
          <strong>{b.diasSeguidosPositivos} dias seguidos entrando mais do que sai.</strong>{" "}
          <Dica texto="O tempo de resposta continua bom enquanto a fila cresce — este é o número que avisa antes." />
        </p>
      ) : (
        <p className="text-sm text-[color:var(--tinta-fraca)]">
          Dias seguidos com saldo positivo: {b.diasSeguidosPositivos}{" "}
          <Dica texto="Saldo positivo por vários dias seguidos significa que entra mais do que sai — o tempo de resposta continua bom enquanto a fila cresce." />
        </p>
      )}

      <div className="vidro flex flex-col gap-1 p-4">
        {b.dias.map((d) => (
          <div key={d.dia} className="flex items-center gap-2 text-xs">
            <span className="w-20 shrink-0 tabular-nums text-[color:var(--tinta-fraca)]">
              {d.dia.slice(5).split("-").reverse().join("/")}
            </span>
            {/* Duas barras, uma em cada direção: entrada e saída lado a lado é o que torna o saldo visível
                sem precisar ler o número. */}
            <span className="flex flex-1 items-center gap-1">
              <span className="flex flex-1 justify-end">
                <span
                  className="h-3 rounded-l bg-[color:var(--acao)]"
                  style={{ width: `${(d.abertas / maior) * 100}%` }}
                />
              </span>
              <span className="flex flex-1">
                <span
                  className="h-3 rounded-r bg-[color:var(--ok)]"
                  style={{ width: `${(d.fechadas / maior) * 100}%` }}
                />
              </span>
            </span>
            <span
              className={
                "w-10 shrink-0 text-right tabular-nums " +
                (d.saldo > 0 ? "text-[color:var(--atencao-texto)]" : "text-[color:var(--tinta-fraca)]")
              }
            >
              {d.saldo > 0 ? `+${d.saldo}` : d.saldo}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-[color:var(--tinta-fraca)]">
        Legenda <Dica texto="Azul é o que abriu, verde é o que fechou. A coluna da direita é o saldo do dia." />
      </p>
    </div>
  );
}

/**
 * Agente de IA (§5.2 da spec 09) — com a divisão que muda qual número se olha.
 *
 * **Escalonar com triagem é sucesso**, não falha: o agente conversou e passou a conversa pronta. Sem triagem
 * é ele largando. Somar os dois faria a operação melhorar e o indicador piorar.
 */
function AgenteAba(props: { readonly dias: number }) {
  const q = useAgenteDeIa(props.dias);

  if (q.isPending) return <EstadoCarregando rotulo="Carregando os números do agente" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  const a = q.data;

  if (a.atendimentosTocados === 0 && a.fluxo.conversasNoMenu === 0) {
    return (
      <EstadoVazio
        titulo="O assistente e o menu não atenderam ninguém neste período"
        comoEncher="Ligue o assistente em Agente Hugi, ou um fluxo em Fluxos. Enquanto estiverem desligados, este número fica em zero — e isso está certo."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm font-medium">O assistente</p>
        <div className="mt-2 flex flex-wrap gap-6">
          <Numero valor={a.atendimentosTocados} rotulo="conversas tocadas" />
          <Numero valor={a.respostas} rotulo="respostas dele" />
          <Numero valor={a.resolvidasSemHumano} rotulo="terminaram sem humano" />
        </div>
      </div>

      <div className="vidro flex flex-col gap-2 p-4">
        <p className="text-sm font-medium">Quando ele passou para uma pessoa <Dica texto="As duas coisas não são iguais, e somá-las é olhar para o número errado." /></p>
        <div className="mt-1 flex flex-wrap gap-6">
          <Numero
            valor={a.escalonadasComTriagem}
            rotulo="com triagem — conversou e passou pronta"
            bom
          />
          <Numero
            valor={a.escalonadasSemTriagem}
            rotulo="sem triagem — largou na primeira"
            atencao={a.escalonadasSemTriagem > 0}
          />
        </div>
        {a.escalonadasSemTriagem > 0 && (
          <p className="text-xs text-[color:var(--atencao-texto)]">
            Largar na primeira aponta buraco nas instruções, não na equipe.
          </p>
        )}
      </div>

      {a.recusas.length > 0 && (
        <div className="vidro flex flex-col gap-2 p-4">
          <p className="text-sm font-medium">Quando ele quis responder e não pôde <Dica texto="É o que diagnostica “está ligado e não faz nada”." /></p>
          <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
            {a.recusas.map((r) => (
              <li key={r.motivo} className="flex items-baseline gap-2 py-1.5 text-sm">
                <span className="w-8 shrink-0 tabular-nums">{r.quantas}</span>
                <span className="text-[color:var(--tinta-fraca)]">{r.motivo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="vidro flex flex-col gap-2 p-4">
        <p className="text-sm font-medium">O menu de triagem <Dica texto="Outra camada, e por isso outro número — ver a spec 11." /></p>
        <div className="mt-1 flex flex-wrap gap-6">
          <Numero valor={a.fluxo.conversasNoMenu} rotulo="entraram no menu" />
          <Numero valor={a.fluxo.encaminhadasPeloMenu} rotulo="escolheram uma opção" bom />
          <Numero
            valor={a.fluxo.desistenciasDoMenu}
            rotulo="não escolheram nada"
            atencao={a.fluxo.desistenciasDoMenu > 0}
          />
        </div>
      </div>

      {/*
        A métrica de DESFECHO — e ela passou a existir.

        Era dita como ausência, porque dependia das tarefas do agente (spec 08 §4). Agora que elas existem, o
        número é o que ele CONCLUIU: transferência que aconteceu, atendimento finalizado com motivo, etiqueta
        aplicada. Resultado, e não esforço.

        `null` continua sendo resposta, e agora significa outra coisa: o cliente não cadastrou tarefa nenhuma.
        Um zero ali leria-se "o assistente não concluiu nada", quando o certo é "ele não tem o que concluir".
      */}
      {a.objetivosConcluidos === null ? (
        <p className="text-xs text-[color:var(--tinta-fraca)]">
          <strong>O assistente ainda não tem tarefas.</strong>{" "}
          <Dica texto="Enquanto ele só responde, esta aba mede esforço. Cadastre uma tarefa no assistente — transferir, finalizar, etiquetar — e o número de objetivos concluídos passa a aparecer aqui." />
        </p>
      ) : (
        <div className="vidro flex flex-col gap-2 p-4">
          <p className="text-sm font-medium">O que ele CONCLUIU <Dica texto="Tarefa executada é resultado, não esforço: uma transferência que aconteceu, um atendimento finalizado com motivo." /></p>
          <div className="mt-1 flex flex-wrap gap-6">
            <Numero valor={a.objetivosConcluidos} rotulo="tarefas concluídas" bom />
            <Numero
              valor={a.tarefasRecusadas}
              rotulo="tentou e não pôde"
              atencao={a.tarefasRecusadas > 0}
            />
            <Numero valor={a.tarefasAguardando} rotulo="esperando o contato confirmar" />
          </div>
          {a.tarefasRecusadas > 0 && (
            <p className="text-xs text-[color:var(--atencao-texto)]">
              “Tentou e não pôde” pede conserto na tarefa.{" "}
              <Dica texto="É diferente de não ter tentado: a tarefa existe, o assistente a chamou, e o domínio recusou." />
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Anúncios.
 *
 * O `referral` da Meta é gravado desde o H7 — nove colunas — e **nunca foi lido**. Chega uma vez só, na
 * primeira mensagem, e a Meta não reenvia: é o dado mais perecível do produto, e o único que liga gasto em
 * mídia a atendimento.
 */
function AnunciosAba(props: { readonly dias: number }) {
  const q = useAnuncios(props.dias);

  if (q.isPending) return <EstadoCarregando rotulo="Carregando os anúncios" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  const a = q.data;

  if (a.itens.length === 0) {
    return (
      <EstadoVazio
        titulo="Nenhuma conversa veio de anúncio neste período"
        comoEncher="Quando alguém clicar num anúncio de Click-to-WhatsApp, a origem chega junto com a primeira mensagem e aparece aqui. Ela chega uma vez só — a Meta não reenvia."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-6">
        <Numero valor={a.conversasComAnuncio} rotulo="conversas vindas de anúncio" />
        <Numero
          valor={
            a.conversasTotais > 0
              ? `${Math.round((a.conversasComAnuncio / a.conversasTotais) * 100)}%`
              : "—"
          }
          rotulo="do total que chegou"
        />
      </div>
      <div className="vidro flex flex-col divide-y divide-[color:var(--borda)] p-4">
        {a.itens.map((i) => (
          <div key={i.anuncioId ?? "sem-id"} className="flex items-baseline gap-3 py-2 text-sm">
            <span className="min-w-0 flex-1">
              {i.titulo ?? <span className="text-[color:var(--tinta-fraca)]">sem título</span>}
              <span className="block font-mono text-xs text-[color:var(--tinta-fraca)]">
                {i.anuncioId ?? "—"}
                {i.tipoDeOrigem ? ` · ${i.tipoDeOrigem}` : ""}
              </span>
            </span>
            <span className="shrink-0 tabular-nums">{i.conversas} conversas</span>
            <span className="shrink-0 tabular-nums text-[color:var(--tinta-fraca)]">
              {i.contatos} contatos
            </span>
            <span className="shrink-0 tabular-nums text-[color:var(--tinta-fraca)]">
              {i.finalizadas} fechadas
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Um número com rótulo. `atencao` e `bom` colorem — e só um dos dois vale por vez. */
function Numero(props: {
  readonly valor: number | string;
  readonly rotulo: string;
  readonly atencao?: boolean;
  readonly bom?: boolean;
}) {
  const cor = props.atencao
    ? "text-[color:var(--atencao-texto)]"
    : props.bom
      ? "text-[color:var(--ok-texto)]"
      : "";
  return (
    <div>
      <p className={`text-2xl font-medium tabular-nums ${cor}`}>{props.valor}</p>
      <p className="max-w-40 text-xs text-[color:var(--tinta-fraca)]">{props.rotulo}</p>
    </div>
  );
}

/**
 * SLA (§5 da spec 09).
 *
 * ## O que esta aba mostra que as outras não
 *
 * **A pausa, em número.** O §3.4 decidiu que o relógio para enquanto o canal recusa saída livre, e declarou
 * o custo: isso cria um jeito de o SLA nunca estourar. O "tempo em espera do cliente" é esse custo, visível —
 * sem ele a pausa seria silenciosa.
 *
 * ## `null` não é 0%
 *
 * Taxa `null` significa "nada foi medido"; 0% significa "nada cumpriu". Mostrar 0% num período sem medida
 * faria a operação parecer péssima num período em que ela não se comprometeu com nada.
 */
function SlaAba(props: { readonly dias: number }) {
  const q = useRelatorioDeSla(props.dias);
  const politicas = usePoliticasDeSla();

  if (q.isPending) return <EstadoCarregando rotulo="Carregando o SLA" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  const s = q.data;
  const nenhumaPolitica = (politicas.data?.itens ?? []).length === 0;

  if (nenhumaPolitica) {
    return (
      <EstadoVazio
        titulo="Você ainda não definiu nenhum compromisso"
        comoEncher="Crie uma política de SLA em Ajustes. Sem política, o Hugi mede quanto demorou — mas não tem contra o que comparar, e nenhuma conversa aparece como fora do prazo."
      />
    );
  }

  if (s.medidos === 0) {
    return (
      <EstadoVazio
        titulo="Nenhuma conversa medida neste período"
        comoEncher="As políticas existem, e nenhuma conversa entrou nelas ainda. O relógio começa quando a conversa entra na fila."
      />
    );
  }

  const horas = Math.round(s.segundosEmEsperaDoCliente / 3600);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-6">
        <Numero valor={s.medidos} rotulo="conversas medidas" />
        <Numero
          valor={s.taxaPrimeiraResposta === null ? "—" : `${s.taxaPrimeiraResposta}%`}
          rotulo="cumpriram a primeira resposta"
          bom={s.taxaPrimeiraResposta !== null && s.taxaPrimeiraResposta >= 90}
          atencao={s.taxaPrimeiraResposta !== null && s.taxaPrimeiraResposta < 70}
        />
        <Numero
          valor={s.taxaResolucao === null ? "—" : `${s.taxaResolucao}%`}
          rotulo="cumpriram a resolução"
          bom={s.taxaResolucao !== null && s.taxaResolucao >= 90}
          atencao={s.taxaResolucao !== null && s.taxaResolucao < 70}
        />
        <Numero
          valor={s.estouradosAgora}
          rotulo="fora do prazo AGORA"
          atencao={s.estouradosAgora > 0}
        />
      </div>

      {(s.taxaPrimeiraResposta === null || s.taxaResolucao === null) && (
        <p className="text-xs text-[color:var(--tinta-fraca)]">
          — é não medido{" "}
          <Dica texto="Um traço significa que nenhuma política mede aquele relógio — não que ninguém cumpriu. Zero por cento e “não medido” são coisas diferentes." />
        </p>
      )}

      {horas > 0 && (
        <div className="vidro flex flex-col gap-1 p-4">
          <p className="text-sm font-medium">{horas}h esperando o cliente <Dica texto="É o tempo em que o relógio ficou parado porque o canal não aceitava resposta livre — fora da janela de 24 horas do WhatsApp, por exemplo. Ele não conta contra a sua equipe, e aparece aqui de propósito: sem isso, a pausa seria um jeito silencioso de o SLA nunca estourar." /></p>
        </div>
      )}

      <div className="vidro flex flex-col gap-2 p-4">
        <p className="text-sm font-medium">Por política</p>
        <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
          {s.porPolitica.map((p) => (
            <li key={p.nome} className="flex items-baseline gap-3 py-1.5 text-sm">
              <span className="min-w-0 flex-1">{p.nome}</span>
              <span className="shrink-0 tabular-nums text-[color:var(--tinta-fraca)]">
                {p.medidos} medidas
              </span>
              <span
                className={
                  "w-24 shrink-0 text-right tabular-nums " +
                  (p.excedidas > 0 ? "text-[color:var(--atencao-texto)]" : "text-[color:var(--tinta-fraca)]")
                }
              >
                {p.excedidas} excedidas
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * O funil do FLUXO (§9 da spec 11).
 *
 * ## O número que esta aba existe para mostrar é a QUEDA, não o volume
 *
 * Volume por passo é um gráfico bonito que não muda nada. O que muda o fluxo é *"aqui 100 pessoas chegaram e
 * 30 seguiram"* — e é por isso que a queda tem destaque visual e o volume não.
 *
 * ## A barra é proporcional ao PRIMEIRO passo
 *
 * Não ao maior. Com condicional, um passo do meio pode ter mais visitas que o de entrada (laço legítimo), e
 * normalizar pelo maior faria o funil parecer que ganha gente no meio.
 */
const ORIGEM_DA_OPORTUNIDADE: Readonly<Record<string, string>> = { campanha: "Campanha", conversa: "Conversa", anuncio: "Anúncio", manual: "Manual" };
const reaisDoFunil = (centavos: number) => `R$ ${(centavos / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * O relatório do funil de oportunidades (spec 10 §3.2): onde a proposta morre. "Perder exige motivo"
 * existe para esta tabela. Valor só aparece quando o servidor o mandou (`read_funnel_values`) — a tela
 * não desenha "R$ 0,00" no lugar de um dado que não chegou.
 */
export function OportunidadesAba(props: { readonly dias: number }) {
  // O recorte por quadro é opcional e nasce em "todos": o relatório do tenant inteiro é o que sempre
  // existiu, e o seletor só aparece quando há mais de um quadro — com um só, ele seria ruído.
  const [quadroId, setQuadroId] = useState<string | null>(null);
  const q = useRelatorioDoFunil(props.dias, quadroId);
  if (q.isPending) return <EstadoCarregando linhas={3} />;
  if (q.error) {
    return (
      <EstadoDaFalha
        falha={falhaDe(q.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
        aoRepetir={() => void q.refetch()}
      />
    );
  }
  const d = q.data!;
  const fechadas = d.ganhas + d.perdidas;
  return (
    <div className="flex flex-col gap-4">
      {d.quadros.length > 1 ? (
        <label className="flex items-center gap-1 text-xs text-[color:var(--tinta-fraca)]">
          Quadro
          <select className="campo compacto text-xs" value={d.quadroId ?? ""} aria-label="Quadro do relatório" onChange={(e) => setQuadroId(e.target.value || null)}>
            <option value="">Todos os quadros</option>
            {d.quadros.map((k) => <option key={k.id} value={k.id}>{k.nome}{k.padrao ? " (padrão)" : ""}</option>)}
          </select>
        </label>
      ) : null}
      {!d.verDoTime ? (
        <p className="text-xs text-[color:var(--tinta-fraca)]">Suas e sem responsável <Dica texto="Você vê as suas oportunidades e as sem responsável. Ver as do time exige read_team_funnel." /></p>
      ) : null}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Cartao rotulo="Abertas agora" valor={d.abertas} />
        <Cartao rotulo="Ganhas no período" valor={d.ganhas} nota={d.verValores && d.valorGanhoCentavos !== null ? reaisDoFunil(d.valorGanhoCentavos) : undefined} />
        <Cartao rotulo="Perdidas no período" valor={d.perdidas} nota={d.verValores && d.valorPerdidoCentavos !== null ? reaisDoFunil(d.valorPerdidoCentavos) : undefined} />
        <div className="vidro p-4">
          <p className="text-xs text-[color:var(--tinta-fraca)]">Taxa de ganho</p>
          <p className="mt-1 text-2xl font-semibold text-[color:var(--tinta-forte)]">{d.taxaDeGanho === null ? "—" : `${Math.round(d.taxaDeGanho * 100)}%`}</p>
          <p className="text-[11px] text-[color:var(--tinta-fraca)]">{fechadas === 0 ? "Nenhuma fechada no período." : `de ${fechadas} fechada(s)`}</p>
        </div>
      </div>

      <Tabela
        titulo="Perdas por motivo"
        nota="Toda perda exige um motivo — é o que faz esta tabela dizer qual objeção fecha mais negócio. Os motivos se editam no funil, em Etapas e motivos."
        vazio="Nenhuma perda no período. Quando alguém marcar uma oportunidade como perdida, o motivo aparece aqui."
        linhas={d.perdasPorMotivo.map((m) => ({
          chave: m.motivo,
          rotulo: m.motivo,
          valor: String(m.quantidade),
          extra: [
            d.perdidas > 0 ? `${Math.round((m.quantidade / d.perdidas) * 100)}% das perdas` : "",
            d.verValores && m.valorCentavos !== null ? reaisDoFunil(m.valorCentavos) : "",
          ].filter(Boolean).join(" · ") || undefined,
        }))}
      />

      <Tabela
        titulo="Por origem"
        nota="De onde o card veio: campanha, anúncio, conversa ou aberto à mão. Abertas é o quadro de agora; ganhas e perdidas são as fechadas no período."
        vazio="Nenhuma oportunidade ainda."
        linhas={d.porOrigem.map((o) => ({
          chave: o.origem,
          rotulo: ORIGEM_DA_OPORTUNIDADE[o.origem] ?? o.origem,
          valor: String(o.abertas + o.ganhas + o.perdidas),
          extra: `${o.abertas} aberta(s) · ${o.ganhas} ganha(s) · ${o.perdidas} perdida(s)`,
        }))}
      />

      {d.previsao ? (
        <div className="vidro flex flex-col gap-2 p-3 text-xs">
          {/* O §3.2 da spec 10: a frase que diz o que o número É, senão previsão vira compromisso. Desde a spec 23, no (i). */}
          <h2 className="text-sm font-semibold">
            Previsão ponderada: {reaisDoFunil(d.previsao.totalCentavos)}{" "}
            <Dica texto="É a soma do valor de cada oportunidade aberta multiplicado pela probabilidade da etapa em que ela está — porcentagens que a sua empresa configurou em Etapas e motivos. Não é compromisso de venda." />
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <p className="font-medium">Por mês da previsão de fechamento</p>
              <ul>
                {d.previsao.porMes.map((m) => (
                  <li key={m.mes ?? "sem"} className="flex justify-between border-t border-[color:var(--borda)] py-0.5">
                    <span>{m.mes ? new Date(`${m.mes}-01T12:00:00`).toLocaleDateString("pt-BR", { month: "long", year: "numeric" }) : "Sem previsão de fechamento"} · {m.cards}</span>
                    <span className="font-medium">{reaisDoFunil(m.centavos)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-medium">Por responsável</p>
              <ul>
                {d.previsao.porResponsavel.map((r) => (
                  <li key={r.responsavelId ?? "sem"} className="flex justify-between border-t border-[color:var(--borda)] py-0.5">
                    <span>{r.nome ?? (r.responsavelId ? "(sem nome no cadastro)" : "Sem responsável")} · {r.cards}</span>
                    <span className="font-medium">{reaisDoFunil(r.centavos)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      {d.diasAteFechar ? (
        <div className="vidro p-3 text-xs">
          <h2 className="text-sm font-semibold">Tempo até fechar</h2>
          <p className="mt-1">
            Mediana <strong>{d.diasAteFechar.mediana} dia(s)</strong>, p90 <strong>{d.diasAteFechar.p90} dia(s)</strong>, em {d.diasAteFechar.amostra} fechada(s) no período — da abertura do card ao ganho ou à perda.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function FunilAba(props: { readonly dias: number }) {
  const q = useFunilDoFluxo(props.dias);

  if (q.isPending) return <EstadoCarregando rotulo="Carregando o funil do fluxo" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  const f = q.data;

  if (f.totais.execucoes === 0) {
    return (
      <EstadoVazio
        titulo="Nenhum fluxo com blocos rodou neste período"
        comoEncher="Esta aba conta execuções de fluxo com blocos — espera, condicional, ação, assistente. O menu de triagem aparece na aba Agente de IA, porque é outra camada."
      />
    );
  }

  const base = f.passos[0]?.execucoes ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-6">
        <Numero valor={f.totais.execucoes} rotulo="execuções no período" />
        <Numero valor={f.totais.terminaram} rotulo="chegaram ao fim" bom />
        <Numero valor={f.totais.esperando} rotulo="ainda no meio" />
        <Numero
          valor={f.totais.estouraram}
          rotulo="estouraram um teto"
          atencao={f.totais.estouraram > 0}
        />
      </div>

      <div className="vidro flex flex-col gap-3 p-4">
        <div>
          <p className="text-sm font-medium">Passo a passo, na ordem em que as pessoas passam <Dica texto="A ordem vem da execução, não do desenho: com condicional, o passo 5 do editor pode ser o primeiro que muita gente vê." /></p>
        </div>

        <ul className="flex flex-col gap-2">
          {f.passos.map((p) => {
            const largura = base === 0 ? 0 : Math.min(100, Math.round((p.execucoes / base) * 100));
            const preocupa = p.quedaPercentual !== null && p.quedaPercentual >= 40;
            const fim = p.tipo === "fim";
            return (
              <li key={p.chave} className="flex flex-col gap-1">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{p.chave}</span>
                  <span className="shrink-0 text-xs text-[color:var(--tinta-fraca)]">
                    {p.tipo} · {p.execucoes} passaram · {p.seguiram} seguiram
                  </span>
                </div>
                <div
                  className="h-2 overflow-hidden rounded-[6px]"
                  style={{ background: "var(--vidro-controle)" }}
                >
                  <div
                    className="h-full rounded-[6px]"
                    style={{
                      width: `${largura}%`,
                      // FUNDO da barra: token semantico. O `-texto` existe para o que se LE.
                      background: preocupa ? "var(--atencao)" : "var(--acao)",
                    }}
                  />
                </div>
                <p
                  className="text-xs"
                  style={{ color: preocupa ? "var(--atencao-texto)" : "var(--tinta-fraca)" }}
                >
                  {fim
                    ? "aqui o fluxo termina — não há queda para medir"
                    : p.quedaPercentual === null
                      ? "— sem passagem suficiente para calcular queda"
                      : `perde ${p.quedaPercentual}% de quem chega aqui`}
                  {preocupa ? " — é o passo para reescrever primeiro" : ""}
                </p>
              </li>
            );
          })}
        </ul>
      </div>

      {f.estouros.length > 0 && (
        <div className="vidro flex flex-col gap-2 p-4">
          <p className="text-sm font-medium">Estouros de teto <Dica texto="Fluxo em laço aparece aqui antes de virar reclamação. Toda conversa que estourou foi para a fila com o motivo no histórico — nenhuma ficou presa." /></p>
          <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
            {f.estouros.map((e) => (
              <li key={e.porque} className="flex items-baseline gap-2 py-1.5 text-sm">
                <span className="w-8 shrink-0 tabular-nums">{e.quantas}</span>
                <span className="text-[color:var(--tinta-fraca)]">{e.porque}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {f.naoConcluiram.length > 0 && (
        <div className="vidro flex flex-col gap-2 p-4">
          <p className="text-sm font-medium">Quem não concluiu <Dica texto="Lista de trabalho, não estatística: são conversas de gente real parada no meio do fluxo." /></p>
          <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
            {f.naoConcluiram.map((n) => (
              <li key={n.atendimentoId} className="flex items-baseline gap-2 py-1.5 text-sm">
                <span className="shrink-0 font-medium">{n.protocolo}</span>
                <span className="truncate text-[color:var(--tinta-fraca)]">
                  {n.contato ?? "sem nome"}
                </span>
                <span className="ml-auto shrink-0 text-xs text-[color:var(--tinta-fraca)]">
                  parada em {n.passoAtual ?? "—"} desde{" "}
                  {new Date(n.desde).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
